import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { CHART_TOOLTIP_STYLE } from '../../../utils/stats-utils';
import type { UserInsight } from '../../../types/insight';

interface PercentileCurveProps {
  insight: UserInsight;
}

interface CurveDatum {
  from: number;
  to: number;
  count: number;
  isUser: boolean;
}

/**
 * Where this climber sits in the cohort.
 *
 * Form is emphasis, not categorical: one series (the cohort) in the
 * de-emphasis gray with a single highlighted bar, because the reader's job is
 * to find themselves, not to tell twenty buckets apart. A single series needs
 * no legend — the card title names it.
 *
 * The highlighted bar is also **directly labelled**, and that is a
 * requirement rather than a flourish: the theme's primary measures 1.96:1
 * against the light surface (below the 3:1 bar), so colour alone cannot carry
 * "you are here". The label is the relief.
 */
export default function PercentileCurve({ insight }: PercentileCurveProps) {
  const { t } = useTranslation();
  const score = insight.index_score;

  const data = useMemo<CurveDatum[]>(() => {
    const buckets = insight.distribution.buckets;

    return buckets.map((bucket, position) => {
      const isLast = position === buckets.length - 1;
      // The top scorer sits exactly on the last bucket's upper edge, which no
      // half-open interval would own.
      const withinBucket =
        score !== null &&
        score >= bucket.from &&
        (isLast ? score <= bucket.to : score < bucket.to);

      return { ...bucket, isUser: withinBucket };
    });
  }, [insight.distribution.buckets, score]);

  if (data.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('userProfile.insights.curve.title')}</CardTitle>
      </CardHeader>
      <CardContent className="px-2 pb-4">
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={data} margin={{ top: 20, right: 8, left: 8, bottom: 0 }} barCategoryGap={2}>
            <XAxis
              dataKey="from"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value: number) => value.toFixed(0)}
              interval="preserveStartEnd"
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickLine={false}
            />
            <Tooltip
              {...CHART_TOOLTIP_STYLE}
              cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
              formatter={(value) => [
                t('userProfile.insights.curve.climbers', { count: Number(value ?? 0) }),
                '',
              ]}
              labelFormatter={(_label, payload) => {
                const datum = payload?.[0]?.payload as CurveDatum | undefined;
                if (!datum) return '';
                return t('userProfile.insights.curve.range', {
                  from: datum.from.toFixed(1),
                  to: datum.to.toFixed(1),
                });
              }}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {data.map((datum, position) => (
                <Cell
                  key={position}
                  fill={
                    datum.isUser
                      ? 'hsl(var(--primary))'
                      : 'hsl(var(--muted-foreground) / 0.35)'
                  }
                />
              ))}
              <LabelList
                dataKey="count"
                content={(props) => {
                  const { x, y, width, index } = props as {
                    x?: number;
                    y?: number;
                    width?: number;
                    index?: number;
                  };
                  if (index === undefined || !data[index]?.isUser) return null;
                  if (x === undefined || y === undefined || width === undefined) return null;

                  return (
                    <text
                      x={x + width / 2}
                      y={y - 6}
                      textAnchor="middle"
                      className="fill-foreground"
                      style={{ fontSize: 11, fontWeight: 600 }}
                    >
                      {t('userProfile.insights.curve.you')}
                    </text>
                  );
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <p className="px-2 text-xs text-muted-foreground">
          {t('userProfile.insights.curve.caption', { count: insight.distribution.cohort_size })}
        </p>
      </CardContent>
    </Card>
  );
}
